import { Inject, Injectable } from "@angular/core";
import { map, Observable, switchMap } from "rxjs";
import { SampleSheetTemplate } from "./biosample-service.types";
import { HttpClient } from "@angular/common/http";
import { PagedResponse } from "@bioconnect-ui/shared-service";
import { StructuredDataService } from "@bioconnect-ui/structured-data-service";

export interface BiosampleConfig {
  restServer: string;
  wijmoLicenseKey: string;
}

@Injectable({
  providedIn: 'root',
})
export class BioSampleService {
  constructor(private httpClient: HttpClient,
    @Inject('bioSampleServiceConfig') private bioSampleServiceConfig: BiosampleConfig,
    private structuredDataService: StructuredDataService
  ) {}

  private restServer = this.bioSampleServiceConfig.restServer

  /**
   * Get an existing sample sheet template
   */
  getTemplate(id: number) {
    return this.httpClient.get<SampleSheetTemplate>(
      `${this.restServer}/metadata_repository/sample_sheet_template/${id}/`,
    );
  }

  /**
   * Get all existing sample sheet templates
   */
  getAllTemplates(): Observable<SampleSheetTemplate[]> {
    return this.httpClient.get<PagedResponse>(
          `${this.restServer}/metadata_repository/sample_sheet_template/?page_size=10000`
        ).pipe(map((result) => <SampleSheetTemplate[]>result.results));
  }

   /**
   * Get templates by filter criteria
   */
  getTemplates( measurementTypeId?: number, filter?: string): Observable<SampleSheetTemplate[]> {
    const params: string[] = [];
    if (filter !== undefined) params.push(`filter=${filter}`);
    if (measurementTypeId !== undefined) params.push(`measurement_type_id=${measurementTypeId}`);
    
    const queryString = params.length > 0 ? `?${params.join('&')}` : '';
    
    return this.httpClient.get(
      `${this.restServer}/metadata_repository/sample_sheet_template/${queryString}`
    ).pipe(map((result) => <SampleSheetTemplate[]>result));
  }


  getPagedTemplates( measurementTypeId?: number, filter?: string, pageNumber?: number, pageSize?: number): Observable<PagedResponse> {
    const params: string[] = [];
    if (filter !== undefined) params.push(`filter=${filter}`);
    if (measurementTypeId !== undefined) params.push(`measurement_type_id=${measurementTypeId}`);
    if (pageNumber !== undefined) params.push(`page=${pageNumber}`);
    if (pageSize !== undefined) params.push(`page_size=${pageSize}`);

    const queryString = params.length > 0 ? `?${params.join('&')}` : '';
    
    return this.httpClient.get<PagedResponse>(
      `${this.restServer}/metadata_repository/sample_sheet_template/${queryString}`
    );
  }
  /**
   * Save a sample sheet template
   */
  addSampleSheetTemplate(template: SampleSheetTemplate): Observable<SampleSheetTemplate> {
    return this.httpClient.post<SampleSheetTemplate>(
      `${this.restServer}/metadata_repository/sample_sheet_template/`,
      template
    );
  }

  /**
   * Update a sample sheet template
   */
  updateSampleSheetTemplate(template: SampleSheetTemplate): Observable<SampleSheetTemplate> {
    return this.httpClient.patch<SampleSheetTemplate>(
      `${this.restServer}/metadata_repository/sample_sheet_template/${template.id}/`,
      template
    );
  }

  /**
   * Delete a sample sheet template
   */
  deleteSampleSheetTemplate(id: number) {
    return this.getTemplate(id).pipe(
      switchMap((template) => {
        const deleteTemplate$ = this.httpClient.delete(
          `${this.restServer}/metadata_repository/sample_sheet_template/${id}/`,
        );

        if (template.data_schema_id) {
          return deleteTemplate$.pipe(
            switchMap(() => this.structuredDataService.deleteDataSchema(template.data_schema_id as number))
          );
        } else {
          return deleteTemplate$;
        }
      })
    );
  }

  /**
   * Get the count of associated assays for each sample template
   * @returns An observable containing the assay counts
   */
  getTemplateCounts(): Observable<any> {
    return this.httpClient.get(
      `${this.restServer}/metadata_repository/sample_sheet_template/template_counts/`
    );
  }
}